import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../redux/store";
import { moveInsectsToDeleted } from "../redux/slices/dataSlice";
import { useTranslation } from 'react-i18next';
import { logger } from '../utils/logger';

interface ProbeDataSessionProps {
  sessionPageNum: number;
  setSessionPageNum: (page: number) => void;
  resetSessionPage: () => void;
}

export default function ProbeDataSession({ 
  sessionPageNum, 
  setSessionPageNum, 
  resetSessionPage 
}: ProbeDataSessionProps) {
  const { t, i18n } = useTranslation(['session', 'common']);
  const dispatch = useDispatch<AppDispatch>();
  const { sessionProbeData, userInfo } = useSelector((state: RootState) => state.data);
  const [navBtnDisable, setNavBtnDisable] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Get current locale for date formatting
  const getDateLocale = () => {
    return i18n.language === 'zh' ? 'zh-CN' : 'en-US';
  };

  // Fix hydration issues
  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (sessionProbeData === null) {
      setNavBtnDisable(true);
    } else {
      setNavBtnDisable(false);
    }
    // Reset image error when session data changes
    setImageError(false);
  }, [sessionProbeData]);

  const handleDelete = () => {
    setShowConfirmation(true);
  };

  const confirmDelete = () => {
    if (sessionProbeData && sessionProbeData[sessionPageNum - 1]) {
      const msg = {
        sessionName: sessionProbeData[sessionPageNum - 1].session,
        trapId: sessionProbeData[sessionPageNum - 1].trapId,
      };
      dispatch(moveInsectsToDeleted(msg));
      setShowConfirmation(false);
      resetSessionPage();
      alert(t('deleteSuccess'));
    }
  };

  const cancelDelete = () => {
    setShowConfirmation(false);
  };

  const handlePrevious = () => {
    if (sessionPageNum > 1) {
      setSessionPageNum(sessionPageNum - 1);
    }
  };

  const handleNext = () => {
    if (sessionProbeData && sessionPageNum < sessionProbeData.length) {
      setSessionPageNum(sessionPageNum + 1);
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString(getDateLocale());
  };

  const convertTemperature = (temp: number) => {
    if (userInfo?.fahrenheit) {
      return ((temp * 9/5) + 32).toFixed(1) + '°F';
    }
    return temp.toFixed(1) + '°C';
  };

  // Prevent hydration mismatch
  if (!mounted) {
    return (
      <div className="flex items-center justify-center h-full">
        <h3 className="text-gray-800 text-lg">{t('common:loading')}</h3>
      </div>
    );
  }

  if (!sessionProbeData || sessionProbeData.length === 0 || !sessionProbeData[sessionPageNum - 1]) {
    return (
      <div className="flex items-center justify-center h-full">
        <h3 className="text-gray-800 text-lg">{t('selectSessionToView')}</h3>
      </div>
    );
  }

  const currentData = sessionProbeData[sessionPageNum - 1];

  return (
    <div className="p-4 h-full flex flex-col overflow-hidden">
      {/* Image */}
      <div className="flex flex-col items-center mb-2 flex-shrink-0">
        <div className="relative w-64 h-64 rounded-full overflow-hidden border border-gray-300 mb-2">
          {currentData.originalFileUrl && !imageError ? (
            <img
              src={currentData.originalFileUrl}
              alt={t('sessionCapture')}
              className="w-full h-full object-cover"
              onError={() => {
                logger.error("Failed to load image:", currentData.originalFileUrl);
                setImageError(true);
              }}
              onLoad={() => setImageError(false)}
            />
          ) : (
            <div className="w-full h-full bg-gray-200 flex items-center justify-center">
              <span className="text-gray-800 text-sm">
                {imageError ? t('failedToLoadImage') : t('noImageAvailable')}
              </span>
            </div>
          )}
        </div>

        {/* Page Navigation */}
        <div className="flex items-center space-x-3">
          <button
            onClick={handlePrevious}
            disabled={navBtnDisable || sessionPageNum <= 1}
            className="px-3 py-1 text-sm bg-blue-500 text-white rounded disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-blue-600 cursor-pointer"
          >
            {t('prev')}
          </button>
          <span className="text-gray-900 text-sm font-medium">
            {sessionPageNum} / {sessionProbeData.length}
          </span>
          <button
            onClick={handleNext}
            disabled={navBtnDisable || sessionPageNum >= sessionProbeData.length}
            className="px-3 py-1 text-sm bg-blue-500 text-white rounded disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-blue-600 cursor-pointer"
          >
            {t('next')}
          </button>
        </div>
      </div>

      {/* Data Grid */}
      <div className="flex-1 min-h-0 grid grid-cols-2 gap-x-3 gap-y-1.5 content-start mt-2">
        <div className="bg-gray-50 px-3 py-1.5 rounded">
          <p className="text-xs font-semibold text-gray-500 leading-tight">{t('trapId')}</p>
          <p className="text-sm text-gray-900 truncate leading-tight">{currentData.trapId}</p>
        </div>
        <div className="bg-gray-50 px-3 py-1.5 rounded">
          <p className="text-xs font-semibold text-gray-500 leading-tight">{t('totalCount')}</p>
          <p className="text-sm text-gray-900 leading-tight">{currentData.particleCount}</p>
        </div>
        <div className="bg-gray-50 px-3 py-1.5 rounded">
          <p className="text-xs font-semibold text-gray-500 leading-tight">{t('building')}</p>
          <p className="text-sm text-gray-900 leading-tight">{currentData.building || '—'}</p>
        </div>
        <div className="bg-gray-50 px-3 py-1.5 rounded">
          <p className="text-xs font-semibold text-gray-500 leading-tight">{t('mothCount')}</p>
          <p className="text-sm text-gray-900 leading-tight">{currentData.insectCollection.moth}</p>
        </div>
        <div className="bg-gray-50 px-3 py-1.5 rounded">
          <p className="text-xs font-semibold text-gray-500 leading-tight">{t('location')}</p>
          <p className="text-sm text-gray-900 leading-tight">{currentData.location || '—'}</p>
        </div>
        <div className="bg-gray-50 px-3 py-1.5 rounded">
          <p className="text-xs font-semibold text-gray-500 leading-tight">{t('beetleCount')}</p>
          <p className="text-sm text-gray-900 leading-tight">{currentData.insectCollection.beetle}</p>
        </div>
        <div className="bg-gray-50 px-3 py-1.5 rounded">
          <p className="text-xs font-semibold text-gray-500 leading-tight">{t('dateTime')}</p>
          <p className="text-sm text-gray-900 leading-tight">{formatDateTime(currentData.createdAt)}</p>
        </div>
        <div className="bg-gray-50 px-3 py-1.5 rounded">
          <p className="text-xs font-semibold text-gray-500 leading-tight">{t('weevilCount')}</p>
          <p className="text-sm text-gray-900 leading-tight">{currentData.insectCollection.weevil}</p>
        </div>
        <div className="bg-gray-50 px-3 py-1.5 rounded">
          <p className="text-xs font-semibold text-gray-500 leading-tight">{t('temperature')}</p>
          <p className="text-sm text-gray-900 leading-tight">{convertTemperature(currentData.temperature)}</p>
        </div>
        <div className="bg-gray-50 px-3 py-1.5 rounded">
          <p className="text-xs font-semibold text-gray-500 leading-tight">{t('otherCount')}</p>
          <p className="text-sm text-gray-900 leading-tight">{currentData.insectCollection.other}</p>
        </div>
        <div className="bg-gray-50 px-3 py-1.5 rounded">
          <p className="text-xs font-semibold text-gray-500 leading-tight">{t('humidity')}</p>
          <p className="text-sm text-gray-900 leading-tight">{currentData.humidity}%</p>
        </div>
        <div className="flex items-center justify-center">
          <button
            onClick={handleDelete}
            className="px-4 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors cursor-pointer w-full"
          >
            {t('deleteSessionAction')}
          </button>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {t('confirmSessionDeletion')}
            </h3>
            <p className="text-gray-900 mb-6">
              {t('deleteConfirmFull')}
            </p>
            <div className="flex space-x-4">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 bg-gray-300 text-gray-900 rounded hover:bg-gray-400 transition-colors cursor-pointer"
              >
                {t('common:cancel')}
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors cursor-pointer"
              >
                {t('common:delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
